'use client';

import { faStar } from '@fortawesome/pro-solid-svg-icons';
import type { RadioProps } from '@heroui/react';
import { useRadio, useRadioGroupContext, VisuallyHidden } from '@heroui/react';
import { Icon } from '@ui';
import React from 'react';
import { cn } from '@/utils';
import { RatingHoverContext } from './rating-hover-context';

const RatingRadioItem = React.forwardRef<HTMLInputElement, RadioProps>((props, ref) => {
  const { Component, isSelected: isSelfSelected, getBaseProps, getInputProps } = useRadio(props);

  const groupContext = useRadioGroupContext();
  const { hoveredValue, setHoveredValue } = React.useContext(RatingHoverContext);

  const isSelected =
    isSelfSelected || Number(groupContext.groupState.selectedValue) >= Number(props.value);
  const isReadOnly = groupContext.groupState.isReadOnly;
  const size = props.size || groupContext.size || 'md';
  const color = props.color || groupContext.color || 'primary';

  const isPreviewing = !isReadOnly && hoveredValue !== null;
  const isFilled = isPreviewing ? Number(hoveredValue) >= Number(props.value) : isSelected;

  const starWidth = React.useMemo(() => {
    switch (size) {
      case 'sm':
        return 16;
      case 'md':
        return 24;
      case 'lg':
        return 32;
    }
  }, [size]);

  const starColor = React.useMemo(() => {
    switch (color) {
      case 'primary':
        return 'text-primary';
      case 'secondary':
        return 'text-secondary';
      case 'success':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'danger':
        return 'text-danger';
      default:
        return 'text-primary';
    }
  }, [color]);

  const ringColor = React.useMemo(() => {
    switch (color) {
      case 'primary':
        return 'group-has-[:focus-visible]:ring-primary';
      case 'secondary':
        return 'group-has-[:focus-visible]:ring-secondary';
      case 'success':
        return 'group-has-[:focus-visible]:ring-success';
      case 'warning':
        return 'group-has-[:focus-visible]:ring-warning';
      case 'danger':
        return 'group-has-[:focus-visible]:ring-danger';
      default:
        return 'group-has-[:focus-visible]:ring-focus';
    }
  }, [color]);

  const baseProps = getBaseProps();

  const handleMouseEnter: typeof baseProps.onMouseEnter = e => {
    baseProps.onMouseEnter?.(e);
    if (!isReadOnly) setHoveredValue(props.value as string);
  };
  const handleMouseLeave: typeof baseProps.onMouseLeave = e => {
    baseProps.onMouseLeave?.(e);
    if (!isReadOnly) setHoveredValue(null);
  };

  return (
    <Component
      {...baseProps}
      ref={ref}
      className={cn(baseProps.className, {
        'cursor-default': isReadOnly,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
      <Icon
        className={cn(
          'transition-transform-colors pointer-events-none rounded-full',
          'group-has-[:focus-visible]:ring-offset-content2 group-has-[:focus-visible]:ring-2 group-has-[:focus-visible]:ring-offset-2',
          ringColor,
          isFilled ? starColor : 'text-default-200',
          {
            'group-data-[pressed=true]:scale-90': !isReadOnly,
          },
        )}
        icon={faStar}
        width={starWidth}
      />
    </Component>
  );
});

RatingRadioItem.displayName = 'RatingRadioItem';

export default RatingRadioItem;
